import audioContext from './sound/audioContext';
import Bounds from './utils/Bounds';

export const EMPTY_IMAGE = new Image();
EMPTY_IMAGE.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

const MAX_PARALLEL_LOADINGS = 16;

let loadingCount = 0;
let user = '';
let password = '';
let crossOrigin = '';
let spritesheetMap = {};
const assetCrossOrigins = {};

const retryErrorMap = {
	'598': true,
	'599': true,
	'429': true
};

const retryDelay = 200;
const offlineRetryDelay = 500;
const maxTryCount = 4;

function loadFile (url: string) {
	loadingCount += 1;

	let retryTempo = retryDelay;
	let tryCount = maxTryCount;
	const fetchRetry = async () => {
		return fetch(url)
			.catch(() => {
				// process interrupted, always retry without decreasing try count
				setTimeout(fetchRetry, retryTempo);
				retryTempo *= 2;
				return fetchRetry();
			})
			.then((response) => {
				// reponse can be converted to following formats:
				// response.blob(); // image
				// response.json(); // json
				// response.text(); // text?
				// response.arrayBuffer(); // sound
				// response.formData(); // ??

				if (response.ok === false) {
					// retrying
					if (window.navigator.onLine === true && retryErrorMap[response.status] === undefined) {
						tryCount -= 1;
					}

					if (tryCount > 0) {
						return fetchRetry();
					}
					// else falling through and returning response
				}

				loadingCount -= 1;
				return response;
			});
	};

	return fetchRetry();
}


async function loadImage (url: string) {
	const response = await loadFile(url);
	if (response.ok === false) {
		return EMPTY_IMAGE;
	}
	
	const image = new Image();
	
	// TODO: properly set crossOrigin
	// const assetCrossOrigin = (assetCrossOrigins[url];
	// if (assetCrossOrigin !== '') {
	//   image.crossOrigin = crossOrigin;
	// } else if (crossOrigin !== '') {
	//   image.crossOrigin = crossOrigin;
	// } else {
	//   image.crossOrigin = 'use-credentials';
	// }
	image.crossOrigin = 'anonymous';

	const imageData = await response.blob();
	const imageSrc = URL.createObjectURL(imageData);

	await new Promise((resolve: (image: HTMLImageElement) => void) => {
		image.onload = () => {
			image.onload = null;
			URL.revokeObjectURL(imageSrc);
			resolve(image);
		};
		image.src = imageSrc;
	});

	return image;
}

async function loadJSON (url: string) {
	 const response = await loadFile(url);
	 const json = await response.json();
	 return json;
}

async function loadSound (url: string) {
	const response = await loadFile(url);
	if (audioContext === null) {
		return null;
	}

	const sound = response.arrayBuffer();

	return new Promise((resolve: (soundBuffer: any) => void) => {
		audioContext.decodeAudioData(sound, (soundBuffer: any) => {
			resolve(soundBuffer);
		});
	});
}

// TODO: use Font abstraction to handle renaming and supersampling
// export class Font {
// 	fontFace: FontFace;
// 	private _name: string;

// 	constructor (fontFace) {
// 		this.fontFace = fontFace;
// 		this._name = '';
// 	}

// 	get name () {
// 		return this._name;
// 	}

// 	set name (name) {
// 		this.fontFace.family = name;
// 		(document as any).fonts.add(this.fontFace);
// 	}
// }

// let fontID = 0;
// async function loadFont (url: string) {
// 	const fontFace = new FontFace('', `url(${url})`);
// 	await fontFace.load();

// 	const font = new Font(fontFace);
// 	font.name = 'apollo-font-' + (fontID++);
// 	return font;
// }

async function loadFont (url: string) {
	const familyMatch = url.match(/\/[^\/]*\./);
	if (familyMatch === null) {
		throw new Error('bad font url!')
	}
	// TODO: check for existing fonts to avoid replacement?
	const family = familyMatch[0];
	const response = await loadFile(url);
	const fontData = await response.arrayBuffer();
	const font = new FontFace(family.substr(1, family.length - 2), fontData);
	(document as any).fonts.add(font);
	return font;
}

export class SpriteImage {
	id: string
	image: HTMLImageElement | HTMLCanvasElement
	bounds: Bounds

	constructor (id: string, image: HTMLImageElement | HTMLCanvasElement, bounds: Bounds) {
		this.id = id;
		this.image = image;
		this.bounds = bounds;
	}
}

export const EMPTY_SPRITE_IMAGE = new SpriteImage('emptyImage', EMPTY_IMAGE, new Bounds(0, 1, 0, 1));

async function loadSprite (url: string) {
	const spritesheetURL = spritesheetMap[url];
	if (spritesheetURL === undefined) {
		// we consider it an image
		const image = await loaders.loadImage(url);
		return new SpriteImage(url, image, new Bounds(0, image.width, 0, image.height));
	}

	// loading image + spriting data in parallel if possible
	const imageLoading = loaders.loadImage(spritesheetURL + '.png');
	const spritingDataLoading = loaders.loadJSON(spritesheetURL + '.json');
	const image = await imageLoading;
	const spritingData = await spritingDataLoading;

	return new SpriteImage(url, image, spritingData[url]);
}

type loadMethod = (url: string) => Promise<any>;
export enum priorities { LOW, MEDIUM, HIGH };

const nullResolve = () => {};
class LoadRequest {
	url: string;
	loader: InternalLoader;
	inProgress: boolean;
	onLoaded: Promise<void>;
	resolve: (asset: any) => void;

	constructor (loader: InternalLoader, url: string) {
		this.url = url;
		this.loader = loader;
		this.inProgress = false;

		this.resolve = nullResolve;
		this.onLoaded = new Promise((resolve) => {
			this.resolve = resolve;
		});
	}

	async perform () {
		// loading asset
		const asset = await this.loader.loadMethod(this.url);

		// adding to loader cache
		this.loader.cache[this.url] = asset;

		// removing this request from loader
		delete this.loader.loadRequests[this.url];

		// returning the asset
		this.resolve(asset);
	}
}

class InternalLoader {
	loadMethod: loadMethod;
	cache: { [url: string]: any; };
	loadRequests: { [url: string]: LoadRequest; };

	constructor (loadMethod: loadMethod) {
		this.loadMethod = loadMethod;
		this.cache = {};
		this.loadRequests = {};
	}

	getLoadRequest (url: string) {
		// Checking for existence of load request
		let loadRequest = this.loadRequests[url];
		if (loadRequest) {
			return loadRequest;
		}

		// Creating promise that will be associated with load request
		// but not making the request
		loadRequest = new LoadRequest(this, url);

		if (!url) {
			loadRequest.inProgress = true;
			loadRequest.resolve(null);
			return loadRequest;
		} 

		// Checking for presence in cache
		let asset = this.cache[url];
		if (asset !== undefined) {
			// Note that the asset could be null
			loadRequest.inProgress = true;
			loadRequest.resolve(asset);
			return loadRequest;
		}

		this.loadRequests[url] = loadRequest;
		return loadRequest;
	}
}

type exposedLoader = (url: string | Array<string>, priority?: priorities) => Promise<any>;
type loaderMap = {
	[key: string]: exposedLoader;
}

const primitivesByExtension = {};
export const loaders: loaderMap = {};
export const primitives = {
	loadImage,
	loadFile,
	loadJSON,
	loadSound
};

interface spritesheetMap {
	[imageURL: string]: string;
}

class Loader {
	private _pendingRequests: object;

	static setCredentials (userName: string, passwordString: string) {
		user = userName;
		password = passwordString;
	}

	static setCrossOrigin (crossOriginString: string) {
		crossOrigin = crossOriginString;
	}

	static setAssetCrossOrigin (url: string, crossOriginString: string) {
		assetCrossOrigins[url] = crossOriginString;
	}

	static setSpritesheetMap (imageToSpritsheetMap: spritesheetMap) {
		// const imageURLs = Object.keys(imageToSpritsheetMap);
		// for (let i = 0; i < imageURLs.length; i++) {
		// 	const imageURL = imageURLs[i];
		// 	spritesheetMap[imageURL] = imageToSpritsheetMap[imageURL];
		// }
		spritesheetMap = imageToSpritsheetMap;
	}

	constructor () {
		this._pendingRequests = {};
		this._pendingRequests[priorities.LOW] = [];
		this._pendingRequests[priorities.MEDIUM] = [];
		this._pendingRequests[priorities.HIGH] = [];

		// /// #if IS_DEVELOPMENT
		// this._logRequests = false;
		// /// #endif
	}

	// /// #if IS_DEVELOPMENT
	// toggleRequests () {
	// 	this._logRequests = !this._logRequests;
	// }
	// /// #endif

	// When images are in spritesheet
	// getImageURL (url: strng) {

	// }

	// release (url: string) {
		// TODO: delete from all the caches of all the internal loaders
	// 	delete this.cache[url];
	// }

	// getLoadPromise (url: string) {
		// TODO: return a Promise.all of all the current requests?
		// also needs a mechanism to 
	// 	return this._getLoadRequest(url).onLoaded;
	// }

	_startPendingRequest (priority: priorities) {
		let pendingRequests = this._pendingRequests[priority];
		if (pendingRequests.length > 0) {
			const loadRequest = pendingRequests.shift();
			this._requestAsset(loadRequest);
			return true;
		}
		return false;
	}

	async _requestAsset (loadRequest: LoadRequest) {
		await loadRequest.perform();

		// /// #if IS_DEVELOPMENT
		// if (this._logRequests) {
		// 	console.warn('Asset Request went through:', url);
		// }
		// /// #endif

		// triggering next request

		// checking high priorities first
		if (this._startPendingRequest(priorities.HIGH)) {
			return;
		}

		// then medium priorities
		if (this._startPendingRequest(priorities.MEDIUM)) {
			return;
		}

		// and finally low priorities
		if (loadingCount === 0) {
			// initiating low priorities only if nothing is loading
			this._startPendingRequest(priorities.LOW);
		}
	}

	_loadAsset (url: string, loader: InternalLoader, priority: priorities = priorities.MEDIUM) {
		if (loader === undefined) {
			console.error('Invalid load method for: ' + url);
			return Promise.resolve(null);
		}

		// /// #if IS_DEVELOPMENT
		// if (this._logRequests) {
		// 	console.warn('Asset Requested:', url);
		// }
		// /// #endif

		const loadRequest = loader.getLoadRequest(url);
		if (loadRequest.inProgress === true) {
			return loadRequest.onLoaded;
		}

		// Making load Request
		loadRequest.inProgress = true;

		const isPipelineFull = loadingCount >= MAX_PARALLEL_LOADINGS;
		if (isPipelineFull || ((loadingCount > 0) && (priority === priorities.LOW))) {
			const requestQueue = this._pendingRequests[priority];
			requestQueue.push(loadRequest);
		} else {
			this._requestAsset(loadRequest);
		}

		return loadRequest.onLoaded;
	}

	_loadAssets (urls: Array<string>, loader: InternalLoader, priority: priorities = priorities.MEDIUM) {
		let assetRequests: Array<Promise<any>> = [];
		for (let u = 0; u < urls.length; u += 1) {
			assetRequests.push(this._loadAsset(urls[u], loader, priority));
		}
		return Promise.all(assetRequests);
	}

	_getLoadMethod (url: string) {
		// Resolving loading method using extension
		const extension = url.substring(url.lastIndexOf('.') + 1).split('?')[0];
		return primitivesByExtension[extension];
	}

	loadAsset (url: string, priority: priorities) {
		return this._loadAsset(url, this._getLoadMethod(url), priority);
	}

	loadAssets (urls: Array<string>, priority: priorities) {
		const assetRequests: Array<Promise<any>> = [];
		for (let u = 0; u < urls.length; u += 1) {
			const url = urls[u];
			assetRequests.push(this._loadAsset(url, this._getLoadMethod(url), priority));
		}
		return Promise.all(assetRequests);
	}
}

const mainLoader = new Loader();

export function createLoader (loadMethod: loadMethod, extensions: Array<string>) {
	const loader = new InternalLoader(loadMethod);

	for (let e = 0; e < extensions.length; e++) {
		primitivesByExtension[extensions[e]] = loader;
	}

	return {
		singular: (url: string, priority?: priorities) => {
			return mainLoader._loadAsset(url, loader, priority);
		},

		plural: (urls: Array<string>, priority?: priorities) => {
			return mainLoader._loadAssets(urls, loader, priority);
		},

		cache: loader.cache
	};
}

const fileLoader = createLoader(loadFile, ['js', 'css', 'html']);
loaders.loadFile = fileLoader.singular;
loaders.loadFiles = fileLoader.plural;

const jsonLoader = createLoader(loadJSON, ['json']);
loaders.loadJSON = jsonLoader.singular;
loaders.loadJSONs = jsonLoader.plural;

const fontLoader = createLoader(loadFont, ['ttf', 'otf']);
loaders.loadFont = fontLoader.singular;
loaders.loadFonts = fontLoader.plural;

const soundLoader = createLoader(loadSound, ['mp3','ogg','mp4','3gp','m4a','aac','flac','mkv','wav']);
loaders.loadSound = soundLoader.singular;
loaders.loadSounds = soundLoader.plural;

const imageLoader = createLoader(loadImage, ['png', 'jpg', 'bmp']);
loaders.loadImage = imageLoader.singular;
loaders.loadImages = imageLoader.plural;

const spriteLoader = createLoader(loadSprite, []);
loaders.loadSprite = spriteLoader.singular;
loaders.loadSprites = spriteLoader.plural;

// const loadFlash = async (url: string) => {
// 	const jsonData: any = await loaders.loadJSON(url + '/data.json');
// 	if (jsonData === null) {
// 		return null;
// 	}

// 	const imagePath = url + '/';
// 	const imageURLs = jsonData.images.map(function (imageURL) {
// 		return imagePath + imageURL;
// 	});

// 	return loaders.loadImages(imageURLs)
// 		.then((images: Array<HTMLImageElement>) => {
// 			const textures = [];
// 			for (let i = 0; i < images.length; i += 1) {
// 				// TODO: make textures out of it
// 			}

// 			return new FlashAnimationData(jsonData, url, images);
// 		});
// }

// const flashLoader = createLoader(loadFlash, ['jfon']);
// loaders.loadFlash = flashLoader.singular;
// loaders.loadFlashes = flashLoader.plural;

export default mainLoader;


// XMLHttpRequest retrying solution
// class XMLHttpRequest2 extends XMLHttpRequest {
// 	constructor () {
// 	  super();
 
// 	  this._onreadystatechange = null;
 
// 	  Object.defineProperty(this, 'onreadystatechange', {
// 		 get () {
// 			return this._onreadystatechange;
// 		 },
 
// 		 set (onreadystatechange) {
 
// 			const onreadystatechangeBound = onreadystatechange;
 
// 			const RETRY_MAP = {
// 			  '598': true,
// 			  '599': true,
// 			  '429': true
// 			};
 
// 			let remainingTriesCount = 5;
// 			let retryTempo = 100;
// 			this._onreadystatechange = function (...args) {
// 			  console.error('args', args)
// 			  console.error('this')
// 			  if (this.readyState !== 4) {
// 				 onreadystatechangeBound();
// 				 return;
// 			  }
 
// 			  if (this.status !== 200) {
// 				 if (!window.navigator.onLine) {
// 					// Retrying without decreasing retry count
// 					const offlineRetryTempo = 350;
// 					setTimeout(() => {
// 					  this.send();
// 					}, offlineRetryTempo);
// 					return;
// 				 }
 
// 				 if (RETRY_MAP[this.status] && remainingTriesCount > 0) {
// 					remainingTriesCount -= 1;
// 					// Retrying
// 					setTimeout(() => {
// 					  this.send();
// 					}, retryTempo);
// 					retryTempo *= 2;
// 					return;
// 				 }
// 			  }
 
// 			  onreadystatechangeBound();
// 			};
 
// 			// this._onreadystatechange = onreadystatechange.bind(this);
// 		 }
// 	  });
 
// 	}
//  }